import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './App.css';
import FormBorder from './components/FormBorder';
import { useDriverRegistration } from './hooks/useDriverRegistration';
import { compressForDatabase, formatFileSize, getBase64Size } from './utils/imageUtils';
import { LANGUAGES, getTranslations } from './translations';

function App() {
  const assets = useMemo(() => ({
    img: process.env.PUBLIC_URL + '/assets/img',
    videos: process.env.PUBLIC_URL + '/assets/img/videos',
    music: process.env.PUBLIC_URL + '/assets/music'
  }), []);

  // Хук для работы с регистрацией водителя
  const {
    loading: apiLoading,
    error: apiError,
    driverId,
    registrationStep: apiRegistrationStep,
    phoneVerified,
    emailVerified,
    sendVerificationCode,
    verifyPhoneCode,
    sendEmailVerificationCode,
    verifyEmailCode,
    createDriver,
    uploadDocument,
    completeRegistration,
    clearError
  } = useDriverRegistration();

  const [started, setStarted] = useState(false);
  const [planetFading, setPlanetFading] = useState(false);
  const [showPlanet, setShowPlanet] = useState(false);
  const [showCybermine, setShowCybermine] = useState(false);
  const [muted, setMuted] = useState(false); // Музыка включена по умолчанию

  const [language, setLanguage] = useState(() => {
    try {
      return localStorage.getItem('dw_lang') || 'ru';
    } catch { return 'ru'; }
  });
  const [isLangDrawerOpen, setIsLangDrawerOpen] = useState(false);
  const [isMenuDrawerOpen, setIsMenuDrawerOpen] = useState(false);
  const t = useMemo(() => getTranslations(language), [language]);

  useEffect(() => {
    try {
      localStorage.setItem('dw_lang', language);
    } catch (_) {}
  }, [language]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    if (!mediaQuery.matches) return;

    const focusSelector = 'input, textarea, [contenteditable="true"]';
    const isFocusTarget = (target) => target && target.matches && target.matches(focusSelector);

    const handleFocusIn = (event) => {
      if (!isFocusTarget(event.target)) return;
      requestAnimationFrame(() => {
        event.target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      });
    };

    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);

  const [showTopTitle, setShowTopTitle] = useState(false);
  const [showProDrivers, setShowProDrivers] = useState(false);
  const [showCornerIcons, setShowCornerIcons] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showSecretBlock, setShowSecretBlock] = useState(false);
  const [hideSecret, setHideSecret] = useState(false);
  const [showFormBlocks, setShowFormBlocks] = useState(false);
  const [showInputForm, setShowInputForm] = useState(false);
  const [showSurnameForm, setShowSurnameForm] = useState(false);
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [showPhoneVerificationForm, setShowPhoneVerificationForm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [userName, setUserName] = useState('');
  const [userSurname, setUserSurname] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showEmailVerificationForm, setShowEmailVerificationForm] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [showRDZone, setShowRDZone] = useState(false);
  const [hideRDZone, setHideRDZone] = useState(false);
  const [showPassportForm, setShowPassportForm] = useState(false);
  const [selectedPassportImage, setSelectedPassportImage] = useState(null);
  const [showPassportBackForm, setShowPassportBackForm] = useState(false);
  const [selectedPassportBackImage, setSelectedPassportBackImage] = useState(null);
  const [showDriverLicenseForm, setShowDriverLicenseForm] = useState(false);
  const [selectedDriverLicenseImage, setSelectedDriverLicenseImage] = useState(null);
  const [showDriverLicenseBackForm, setShowDriverLicenseBackForm] = useState(false);
  const [selectedDriverLicenseBackImage, setSelectedDriverLicenseBackImage] = useState(null);
  const [showSelfieForm, setShowSelfieForm] = useState(false);
  const [selectedSelfieImage, setSelectedSelfieImage] = useState(null);
  const [showRDAZone, setShowRDAZone] = useState(false);
  const [hideRDAZone, setHideRDAZone] = useState(false);
  const [showCarBrandForm, setShowCarBrandForm] = useState(false);
  const [selectedCarBrand, setSelectedCarBrand] = useState('BMW');
  const [carBrandIndex, setCarBrandIndex] = useState(0);
  const [showCarModelForm, setShowCarModelForm] = useState(false);
  const [selectedCarModel, setSelectedCarModel] = useState('');
  const [carModelIndex, setCarModelIndex] = useState(0);
  const [showCarColorForm, setShowCarColorForm] = useState(false);
  const [selectedCarColor, setSelectedCarColor] = useState('');
  const [carColorIndex, setCarColorIndex] = useState(0);
  const [showCarYearForm, setShowCarYearForm] = useState(false);
  const [selectedCarYear, setSelectedCarYear] = useState('');
  const [carYearIndex, setCarYearIndex] = useState(0);
  const [showCarLicensePlateForm, setShowCarLicensePlateForm] = useState(false);
  const [carLicensePlate, setCarLicensePlate] = useState('');
  const [showCarTechPassportForm, setShowCarTechPassportForm] = useState(false);
  const [selectedCarTechPassportImage, setSelectedCarTechPassportImage] = useState(null);
  const [showRegistrationComplete, setShowRegistrationComplete] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // удалено: lastScrollTime
  const [dragStartY, setDragStartY] = useState(0);
  // удалено: dragAccumulator
  // удалено: isAnimating
  const [rotationOffset, setRotationOffset] = useState(0);
  // Физическая модель вращения для барабана марок
  const angularVelocityRef = useRef(0);
  const rotationResidualRef = useRef(0);
  const isDraggingRef = useRef(false);
  const carBrandIndexRef = useRef(carBrandIndex);
  
  // Физическая модель вращения для барабана моделей
  const modelAngularVelocityRef = useRef(0);
  const modelRotationResidualRef = useRef(0);
  const isModelDraggingRef = useRef(false);
  const carModelIndexRef = useRef(carModelIndex);
  
  // Физическая модель вращения для барабана цветов
  const colorAngularVelocityRef = useRef(0);
  const colorRotationResidualRef = useRef(0);
  const isColorDraggingRef = useRef(false);
  const carColorIndexRef = useRef(carColorIndex);
  
  // Физическая модель вращения для барабана годов
  const yearAngularVelocityRef = useRef(0);
  const yearRotationResidualRef = useRef(0);
  const isYearDraggingRef = useRef(false);
  const carYearIndexRef = useRef(carYearIndex);

  const carBrands = useMemo(
    () => [
      'BMW',
      'MERCEDES',
      'AUDI',
      'VOLKSWAGEN',
      'SKODA',
      'TOYOTA',
      'HONDA',
      'NISSAN',
      'FORD',
      'CHEVROLET',
      'HYUNDAI',
    ],
    []
  );
  
  const carModels = useMemo(() => ({
    'BMW': ['X1', 'X3', 'X5', 'X7', '3 SERIES', '5 SERIES', '7 SERIES', 'Z4', 'M3', 'M5'],
    'MERCEDES': ['A-CLASS', 'C-CLASS', 'E-CLASS', 'S-CLASS', 'GLA', 'GLC', 'GLE', 'GLS', 'AMG GT', 'G-CLASS'],
    'AUDI': ['A3', 'A4', 'A6', 'A8', 'Q3', 'Q5', 'Q7', 'Q8', 'TT', 'R8'],
    'VOLKSWAGEN': ['GOLF', 'PASSAT', 'TIGUAN', 'TOUAREG', 'POLO', 'JETTA', 'ARTEON', 'ID.4', 'BEETLE', 'ATLAS'],
    'SKODA': [
      'OCTAVIA',
      'SUPERB',
      'KODIAQ',
      'KAROQ',
      'KAMIQ',
      'FABIA',
      'SCALA',
      'ENYAQ',
      'KUSHAQ',
      'RAPID',
    ],
    'TOYOTA': ['CAMRY', 'COROLLA', 'RAV4', 'HIGHLANDER', 'PRIUS', 'LAND CRUISER', 'TACOMA', 'TUNDRA', 'SIENNA', 'AVALON'],
    'HONDA': ['CIVIC', 'ACCORD', 'CR-V', 'PILOT', 'FIT', 'HR-V', 'PASSPORT', 'RIDGELINE', 'INSIGHT', 'ODYSSEY'],
    'NISSAN': ['SENTRA', 'ALTIMA', 'MAXIMA', 'ROGUE', 'MURANO', 'PATHFINDER', 'ARMADA', 'FRONTIER', 'TITAN', '370Z'],
    'FORD': ['FOCUS', 'FUSION', 'MUSTANG', 'ESCAPE', 'EXPLORER', 'EXPEDITION', 'F-150', 'RANGER', 'BRONCO', 'EDGE'],
    'CHEVROLET': ['CRUZE', 'MALIBU', 'IMPALA', 'EQUINOX', 'TRAVERSE', 'TAHOE', 'SUBURBAN', 'SILVERADO', 'CAMARO', 'CORVETTE'],
    'HYUNDAI': ['ELANTRA', 'SONATA', 'AZERA', 'TUCSON', 'SANTA FE', 'PALISADE', 'KONA', 'VENUE', 'VELOSTER', 'GENESIS']
  }), []);
  
  const carColors = useMemo(() => [
    'БЕЛЫЙ', 'ЧЕРНЫЙ', 'СЕРЫЙ', 'СЕРЕБРИСТЫЙ', 'КРАСНЫЙ', 
    'СИНИЙ', 'ЗЕЛЕНЫЙ', 'ЖЕЛТЫЙ', 'ОРАНЖЕВЫЙ', 'КОРИЧНЕВЫЙ',
    'БЕЖЕВЫЙ', 'ЗОЛОТИСТЫЙ', 'БОРДОВЫЙ', 'ФИОЛЕТОВЫЙ', 'РОЗОВЫЙ'
  ], []);
  
  const carYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Годы от 1950 до текущего года + 1 (для будущих моделей)
    for (let year = currentYear + 1; year >= 1950; year--) {
      years.push(year.toString());
    }
    return years;
  }, []);
  
  const [modelRotationOffset, setModelRotationOffset] = useState(0);
  const [colorRotationOffset, setColorRotationOffset] = useState(0);
  const [yearRotationOffset, setYearRotationOffset] = useState(0);

  const planetRef = useRef(null);
  const cybermineRef = useRef(null);
  const musicRef = useRef(null);

  useEffect(() => {
    if (!started) return;
    setShowPlanet(true);
  }, [started]);

  useEffect(() => {
    const v = planetRef.current;
    if (!v) return;
    if (showPlanet) {
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  }, [showPlanet]);

  useEffect(() => {
    const v = cybermineRef.current;
    if (!v) return;
    v.muted = muted;
  }, [muted]);

  // Управление музыкой при регистрации
  useEffect(() => {
    const audio = musicRef.current;
    if (!audio) return;
    audio.muted = muted;
  }, [muted]);

  // Управление музыкой при изменении состояния muted
  useEffect(() => {
    const audio = musicRef.current;
    if (!audio) return;
    if (showWelcome || showSecretBlock || showFormBlocks || showInputForm) {
      // Если музыка должна играть (после WELCOME)
      if (!muted && audio.paused) {
        audio.play().catch((err) => {
          console.log('Не удалось воспроизвести музыку:', err);
        });
      } else if (muted && !audio.paused) {
        audio.pause();
      }
    }
  }, [muted, showWelcome, showSecretBlock, showFormBlocks, showInputForm]);

  const handlePlanetTime = () => {
    const v = planetRef.current;
    if (!v || planetFading) return;
    if (v.duration && v.currentTime >= Math.max(0, v.duration - 1)) {
      setPlanetFading(true);
      // Подготовим фон: запускаем второе видео под оверлеем заранее
      setShowCybermine(true);
    }
  };

  const handlePlanetEnded = () => {
    setShowPlanet(false);
    setPlanetFading(false);
    setShowCybermine(true);
    const TITLE_START_MS = 1200;
    const TITLE_DURATION_MS = 4000;
    const PRO_START_MS = TITLE_START_MS + TITLE_DURATION_MS + 200;
    const PRO_DURATION_MS = 4000;
    const ICONS_START_MS = PRO_START_MS + PRO_DURATION_MS + 200;
    const WELCOME_START_MS = ICONS_START_MS + 1000;
    const WELCOME_DURATION_MS = 6000; // Сократили с 8000 до 6000
    const SECRET_START_MS = WELCOME_START_MS + WELCOME_DURATION_MS - 1000; // Начинаем на 1 секунду раньше чем кончается welcome
    const SECRET_VISIBLE_MS = 3000;
    const SECRET_HIDE_MS = 2000;

    setTimeout(() => { setShowTopTitle(true); }, TITLE_START_MS);
    setTimeout(() => { setShowProDrivers(true); }, PRO_START_MS);
    setTimeout(() => { setShowCornerIcons(true); }, ICONS_START_MS);
    setTimeout(() => { 
      setShowWelcome(true);
      // Запускаем музыку одновременно с появлением текста WELCOME
      const audio = musicRef.current;
      if (audio && !muted) {
        audio.currentTime = 0;
        audio.play().catch((err) => {
          console.log('Не удалось воспроизвести музыку:', err);
        });
      }
    }, WELCOME_START_MS);
    setTimeout(() => {
      setShowWelcome(false);
      setShowSecretBlock(true);
    }, SECRET_START_MS);
    setTimeout(() => {
      setHideSecret(true);
      setTimeout(() => {
        setShowSecretBlock(false);
        setHideSecret(false);
        setShowFormBlocks(true);
      }, SECRET_HIDE_MS);
    }, SECRET_START_MS + SECRET_VISIBLE_MS);
  };

  const handleStart = () => {
    if (started) return;
    setStarted(true);
  };

  useEffect(() => {
    const v = cybermineRef.current;
    if (!v) return;
    if (showCybermine) {
      v.currentTime = 0;
      v.play().catch(() => {});
      // Оптимизация для плавного зацикливания
      const handleTimeUpdate = () => {
        if (v.duration && v.currentTime >= v.duration - 0.1) {
          v.currentTime = 0;
        }
      };
      v.addEventListener('timeupdate', handleTimeUpdate);
      return () => v.removeEventListener('timeupdate', handleTimeUpdate);
    } else {
      v.pause();
    }
  }, [showCybermine]);

  const toggleMute = () => {
    setMuted((m) => !m);
  };

  const handleAgreeClick = () => {
    setShowFormBlocks(false);
    setShowInputForm(true);
  };

  const handleInputChange = (e) => {
    setUserName(e.target.value);
  };

  const handleSurnameChange = (e) => {
    setUserSurname(e.target.value);
  };

  const handleArrowClick = () => {
    setShowInputForm(false);
    setShowSurnameForm(true);
  };

  const handleSurnameArrowClick = () => {
    setShowSurnameForm(false);
    setShowPhoneForm(true);
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // только цифры
    if (value.length > 9) value = value.slice(0, 9); // максимум 9 цифр
    setUserPhone(value);
  };

  const handlePhoneArrowClick = () => {
    const fullPhone = `+41${userPhone}`;
    sendVerificationCode(fullPhone)
      .then(() => {
        setShowPhoneForm(false);
        setShowPhoneVerificationForm(true);
      })
      .catch((error) => {
        alert('Не удалось отправить код: ' + error.message);
      });
  };

  const handlePhoneVerificationChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 6) value = value.slice(0, 6);
    setPhoneVerificationCode(value);
  };

  const handlePhoneVerificationArrowClick = () => {
    const fullPhone = `+41${userPhone}`;
    verifyPhoneCode(fullPhone, phoneVerificationCode)
      .then(() => {
        setShowPhoneVerificationForm(false);
        setShowEmailForm(true);
      })
      .catch((error) => {
        alert('Неверный код: ' + error.message);
      });
  };

  const handleEmailChange = (e) => {
    setUserEmail(e.target.value);
    setEmailVerificationCode('');
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailArrowClick = () => {
    sendEmailVerificationCode(userEmail)
      .then(() => {
        setShowEmailForm(false);
        setShowEmailVerificationForm(true);
      })
      .catch((error) => {
        alert('Не удалось отправить код на email: ' + error.message);
      });
  };

  const handleEmailVerificationChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 6) value = value.slice(0, 6);
    setEmailVerificationCode(value);
  };

  const handleEmailVerificationArrowClick = () => {
    verifyEmailCode(userEmail, emailVerificationCode)
      .then(() => {
        setShowEmailVerificationForm(false);
        setShowRDZone(true);
      })
      .catch((error) => {
        alert('Неверный код email: ' + error.message);
      });
  };

  const handleRDZoneClick = () => {
    setHideRDZone(true);
    setTimeout(() => {
      setShowRDZone(false);
      setHideRDZone(false);
      setShowPassportForm(true);
    }, 1000);
  };

  // Автоматическое завершение анимации RD зоны
  useEffect(() => {
    if (showRDZone && !hideRDZone) {
      const timer = setTimeout(() => {
        setHideRDZone(true);
        setTimeout(() => {
          setShowRDZone(false);
          setHideRDZone(false);
          setShowPassportForm(true);
        }, 1000);
      }, 3000); // 3 секунды на показ анимации
      
      return () => clearTimeout(timer);
    }
  }, [showRDZone, hideRDZone]);

  // Автоматическое завершение анимации RDA зоны
  useEffect(() => {
    if (showRDAZone && !hideRDAZone) {
      const timer = setTimeout(() => {
        setHideRDAZone(true);
        setTimeout(() => {
          setShowRDAZone(false);
          setHideRDAZone(false);
          setShowCarBrandForm(true);
        }, 1000);
      }, 3000); // 3 секунды на показ анимации
      
      return () => clearTimeout(timer);
    }
  }, [showRDAZone, hideRDAZone]);



  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        console.log(`📸 Загружен паспорт, оригинальный размер: ${formatFileSize(file.size)}`);
        
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const compressed = await compressForDatabase(e.target.result);
            console.log(`✅ Паспорт сжат до: ${formatFileSize(getBase64Size(compressed))}`);
            setSelectedPassportImage(compressed);
          } catch (error) {
            console.error('❌ Ошибка сжатия паспорта:', error);
            // В случае ошибки используем оригинал
            setSelectedPassportImage(e.target.result);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('❌ Ошибка загрузки паспорта:', error);
      }
    }
  };

  const handlePassportBackUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        console.log(`📸 Загружен паспорт (обратная), оригинальный размер: ${formatFileSize(file.size)}`);
        
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const compressed = await compressForDatabase(e.target.result);
            console.log(`✅ Паспорт (обратная) сжат до: ${formatFileSize(getBase64Size(compressed))}`);
            setSelectedPassportBackImage(compressed);
          } catch (error) {
            console.error('❌ Ошибка сжатия паспорта (обратная):', error);
            setSelectedPassportBackImage(e.target.result);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('❌ Ошибка загрузки паспорта (обратная):', error);
      }
    }
  };

  const handlePassportNext = () => {
    // Просто переходим к следующему шагу, документы загрузим в конце
    console.log('📄 Паспорт (лицевая) выбран, переходим к обратной стороне');
    setShowPassportForm(false);
    setShowPassportBackForm(true);
  };

  const handlePassportBackNext = () => {
    // Просто переходим к следующему шагу
    console.log('📄 Паспорт (обратная) выбран, переходим к правам');
    setShowPassportBackForm(false);
    setShowDriverLicenseForm(true);
  };

  const handleDriverLicenseNext = () => {
    // Просто переходим к следующему шагу
    console.log('📄 Права (лицевая) выбраны, переходим к обратной стороне');
    setShowDriverLicenseForm(false);
    setShowDriverLicenseBackForm(true);
  };

  const handleDriverLicenseBackNext = () => {
    // Просто переходим к следующему шагу
    console.log('📄 Права (обратная) выбраны, переходим к селфи');
    setShowDriverLicenseBackForm(false);
    setShowSelfieForm(true);
  };

  const handleDriverLicenseUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        console.log(`📸 Загружены права, оригинальный размер: ${formatFileSize(file.size)}`);
        
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const compressed = await compressForDatabase(e.target.result);
            console.log(`✅ Права сжаты до: ${formatFileSize(getBase64Size(compressed))}`);
            setSelectedDriverLicenseImage(compressed);
          } catch (error) {
            console.error('❌ Ошибка сжатия прав:', error);
            setSelectedDriverLicenseImage(e.target.result);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('❌ Ошибка загрузки прав:', error);
      }
    }
  };

  const handleDriverLicenseBackUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        console.log(`📸 Загружены права (обратная), оригинальный размер: ${formatFileSize(file.size)}`);
        
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const compressed = await compressForDatabase(e.target.result);
            console.log(`✅ Права (обратная) сжаты до: ${formatFileSize(getBase64Size(compressed))}`);
            setSelectedDriverLicenseBackImage(compressed);
          } catch (error) {
            console.error('❌ Ошибка сжатия прав (обратная):', error);
            setSelectedDriverLicenseBackImage(e.target.result);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('❌ Ошибка загрузки прав (обратная):', error);
      }
    }
  };

  const handleSelfieUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        console.log(`📸 Загружено селфи, оригинальный размер: ${formatFileSize(file.size)}`);
        
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const compressed = await compressForDatabase(e.target.result);
            console.log(`✅ Селфи сжато до: ${formatFileSize(getBase64Size(compressed))}`);
            setSelectedSelfieImage(compressed);
          } catch (error) {
            console.error('❌ Ошибка сжатия селфи:', error);
            setSelectedSelfieImage(e.target.result);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('❌ Ошибка загрузки селфи:', error);
      }
    }
  };

  const handleSelfieNext = () => {
    // Просто переходим к следующему шагу
    console.log('📄 Селфи выбрано, переходим к данным автомобиля');
    setShowSelfieForm(false);
    setShowRDAZone(true);
  };

  const handleRDAZoneClick = () => {
    setHideRDAZone(true);
    setTimeout(() => {
      setShowRDAZone(false);
      setHideRDAZone(false);
      setShowCarBrandForm(true);
    }, 1000);
  };

  const handleCarBrandNext = () => {
    // Переход от выбора марки к выбору модели
    setShowCarBrandForm(false);
    setShowCarModelForm(true);
    // Инициализируем модель для выбранной марки
    const currentModels = carModels[selectedCarBrand] || [];
    if (currentModels.length > 0) {
      setSelectedCarModel(currentModels[0]);
      setCarModelIndex(0);
    }
  };

  const handleCarModelNext = () => {
    // Переход от выбора модели к выбору цвета
    setShowCarModelForm(false);
    setShowCarColorForm(true);
    // Инициализируем цвет
    if (carColors.length > 0) {
      setSelectedCarColor(carColors[0]);
      setCarColorIndex(0);
    }
  };

  const handleCarColorNext = () => {
    // Переход от выбора цвета к выбору года
    setShowCarColorForm(false);
    setShowCarYearForm(true);
    // Инициализируем год (текущий год)
    if (carYears.length > 0) {
      setSelectedCarYear(carYears[0]);
      setCarYearIndex(0);
    }
  };

  const handleCarYearNext = () => {
    // Переход от выбора года к вводу госномера
    setShowCarYearForm(false);
    setShowCarLicensePlateForm(true);
  };

  const handleCarLicensePlateNext = async () => {
    // Создаем водителя в БД когда у нас есть все данные
    if (!driverId && !apiLoading) {
      try {
        await createDriver({
          phone: userPhone,
          phone_verified: phoneVerified,
          email_verified: emailVerified,
          first_name: userName,
          last_name: userSurname,
          email: userEmail,
          car_brand: selectedCarBrand,
          car_model: selectedCarModel,
          car_color: selectedCarColor,
          car_year: selectedCarYear,
          car_license_plate: carLicensePlate
        });
        
        console.log('✅ Водитель создан в БД с полными данными, ID:', driverId);
      } catch (error) {
        console.error('❌ Ошибка создания водителя:', error);
        alert('Ошибка сохранения данных: ' + error.message);
        return; // Не переходим дальше если не удалось создать водителя
      }
    }

    // Переход от ввода госномера к фото техпаспорта
    setShowCarLicensePlateForm(false);
    setShowCarTechPassportForm(true);
  };

  const handleCarTechPassportImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        console.log(`📸 Загружен техпаспорт, оригинальный размер: ${formatFileSize(file.size)}`);
        
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const compressed = await compressForDatabase(e.target.result);
            console.log(`✅ Техпаспорт сжат до: ${formatFileSize(getBase64Size(compressed))}`);
            setSelectedCarTechPassportImage(compressed);
          } catch (error) {
            console.error('❌ Ошибка сжатия техпаспорта:', error);
            setSelectedCarTechPassportImage(e.target.result);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('❌ Ошибка загрузки техпаспорта:', error);
      }
    }
  };

  const handleCarTechPassportNext = async () => {
    console.log('🚀 НАЧИНАЕМ ФИНАЛЬНЫЙ ЭТАП РЕГИСТРАЦИИ');
    console.log('Driver ID:', driverId);
    console.log('Selected images check:');
    console.log('- Passport front:', !!selectedPassportImage);
    console.log('- Passport back:', !!selectedPassportBackImage);
    console.log('- License front:', !!selectedDriverLicenseImage);
    console.log('- License back:', !!selectedDriverLicenseBackImage);
    console.log('- Selfie:', !!selectedSelfieImage);
    console.log('- Tech passport:', !!selectedCarTechPassportImage);
    
    if (!driverId) {
      console.error('❌ Водитель не создан, не можем загружать документы');
      alert('Ошибка: водитель не создан в БД. Попробуйте заново.');
      return;
    }

    console.log('🔄 Начинаем загрузку всех документов для водителя ID:', driverId);

    try {
      // Загружаем все документы последовательно
      if (selectedPassportImage) {
        await uploadDocument('passport_front', selectedPassportImage, 'passport_front.jpg');
        console.log('✅ Паспорт (лицевая) загружен');
      }

      if (selectedPassportBackImage) {
        await uploadDocument('passport_back', selectedPassportBackImage, 'passport_back.jpg');
        console.log('✅ Паспорт (обратная) загружен');
      }

      if (selectedDriverLicenseImage) {
        await uploadDocument('driver_license_front', selectedDriverLicenseImage, 'driver_license_front.jpg');
        console.log('✅ Права (лицевая) загружены');
      }

      if (selectedDriverLicenseBackImage) {
        await uploadDocument('driver_license_back', selectedDriverLicenseBackImage, 'driver_license_back.jpg');
        console.log('✅ Права (обратная) загружены');
      }

      if (selectedSelfieImage) {
        await uploadDocument('selfie', selectedSelfieImage, 'selfie.jpg');
        console.log('✅ Селфи загружено');
      }

      if (selectedCarTechPassportImage) {
        await uploadDocument('car_tech_passport', selectedCarTechPassportImage, 'car_tech_passport.jpg');
        console.log('✅ Техпаспорт загружен');
      }

      // Завершаем регистрацию
      console.log('🏁 Вызываем completeRegistration...');
      const completionResult = await completeRegistration();
      console.log('🎉 Результат завершения регистрации:', completionResult);
      console.log('🎉 Данные результата:', completionResult?.data);
      console.log('🎉 Credentials:', completionResult?.data?.credentials);

      const creds = completionResult?.data?.credentials;
      const emailSent = Boolean(completionResult?.data?.email_sent);
      if (creds?.driver_code && creds?.password) {
        console.log('🔑 УЧЕТНЫЕ ДАННЫЕ ДЛЯ ВХОДА:');
        console.log(`📧 Email: ${creds.email}`);
        console.log(`🏷️ Позывной: ${creds.driver_code}`);
        console.log(`🔐 Пароль: ${creds.password}`);
        if (!creds?.email) {
          console.log('📧 Email не был указан — письмо не отправлялось.');
        } else if (emailSent) {
          console.log('📧 На email отправлено письмо с данными для входа (проверьте Spam).');
        } else {
          console.log('📧 Автоотправка письма не удалась — сохраните данные из консоли или свяжитесь с поддержкой.');
        }
      } else {
        console.warn('⚠️ Учетные данные не найдены в ответе:', creds);
        const driverCode = completionResult?.data?.driver_code;
        if (driverCode) {
          console.log('🏷️ Позывной:', driverCode);
        }
      }

      console.log('🎉 Регистрация полностью завершена!');

    } catch (error) {
      console.error('❌ Ошибка при загрузке документов:', error);
      console.error('Полная ошибка:', error);
      alert('Ошибка загрузки документов: ' + error.message);
    }

    // Переход к финальному экрану
    setShowCarTechPassportForm(false);
    setShowRegistrationComplete(true);
  };

  // Синхронизация refs со стейтом для барабана марок
  useEffect(() => { carBrandIndexRef.current = carBrandIndex; }, [carBrandIndex]);
  useEffect(() => { rotationResidualRef.current = rotationOffset; }, [rotationOffset]);
  
  // Синхронизация refs со стейтом для барабана моделей
  useEffect(() => { carModelIndexRef.current = carModelIndex; }, [carModelIndex]);
  useEffect(() => { modelRotationResidualRef.current = modelRotationOffset; }, [modelRotationOffset]);
  
  // Синхронизация refs со стейтом для барабана цветов
  useEffect(() => { carColorIndexRef.current = carColorIndex; }, [carColorIndex]);
  useEffect(() => { colorRotationResidualRef.current = colorRotationOffset; }, [colorRotationOffset]);
  
  // Синхронизация refs со стейтом для барабана годов
  useEffect(() => { carYearIndexRef.current = carYearIndex; }, [carYearIndex]);
  useEffect(() => { yearRotationResidualRef.current = yearRotationOffset; }, [yearRotationOffset]);

  // Цикл анимации с инерцией
  useEffect(() => {
    if (!showCarBrandForm) return;
    const STEP = 0.4; // радиан на один элемент
    let rafId = 0;
    let last = performance.now();

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      // Текущее состояние
      let av = angularVelocityRef.current; // рад/с
      let residual = rotationResidualRef.current; // рад

      // Трение (если не тащим мышью)
      if (!isDraggingRef.current) {
        const dampingPerSecond = 2.8; // ещё мягче
        av = av * Math.max(0, 1 - dampingPerSecond * dt);
        if (Math.abs(av) < 0.001) av = 0;
      }

      // Интеграция
      residual += av * dt;

      // Щелчок по шагам и обновление индекса
      let index = carBrandIndexRef.current;
      const half = STEP / 2;
      while (residual >= half) {
        residual -= STEP;
        index = (index + 1) % carBrands.length;
      }
      while (residual <= -half) {
        residual += STEP;
        index = (index - 1 + carBrands.length) % carBrands.length;
      }

      if (index !== carBrandIndexRef.current) {
        carBrandIndexRef.current = index;
        setCarBrandIndex(index);
        setSelectedCarBrand(carBrands[index]);
      }

      // Записываем обратно и рендерим
      angularVelocityRef.current = av;
      rotationResidualRef.current = residual;
      setRotationOffset(residual);

      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [showCarBrandForm, carBrands]);

  // Цикл анимации с инерцией для барабана моделей
  useEffect(() => {
    if (!showCarModelForm) return;
    const STEP = 0.4; // радиан на один элемент
    let rafId = 0;
    let last = performance.now();

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      // Текущее состояние
      let av = modelAngularVelocityRef.current; // рад/с
      let residual = modelRotationResidualRef.current; // рад

      // Трение (если не тащим мышью)
      if (!isModelDraggingRef.current) {
        const dampingPerSecond = 2.8; // такое же затухание как у марок
        av = av * Math.max(0, 1 - dampingPerSecond * dt);
        if (Math.abs(av) < 0.001) av = 0;
      }

      // Интеграция
      residual += av * dt;

      // Щелчок по шагам и обновление индекса
      const currentModels = carModels[selectedCarBrand] || [];
      let index = carModelIndexRef.current;
      const half = STEP / 2;
      while (residual >= half) {
        residual -= STEP;
        index = (index + 1) % currentModels.length;
      }
      while (residual <= -half) {
        residual += STEP;
        index = (index - 1 + currentModels.length) % currentModels.length;
      }

      if (index !== carModelIndexRef.current && currentModels.length > 0) {
        carModelIndexRef.current = index;
        setCarModelIndex(index);
        setSelectedCarModel(currentModels[index]);
      }

      // Записываем обратно и рендерим
      modelAngularVelocityRef.current = av;
      modelRotationResidualRef.current = residual;
      setModelRotationOffset(residual);

      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [showCarModelForm, carModels, selectedCarBrand]);

  // Цикл анимации с инерцией для барабана цветов
  useEffect(() => {
    if (!showCarColorForm) return;
    const STEP = 0.4; // радиан на один элемент
    let rafId = 0;
    let last = performance.now();

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      // Текущее состояние
      let av = colorAngularVelocityRef.current; // рад/с
      let residual = colorRotationResidualRef.current; // рад

      // Трение (если не тащим мышью)
      if (!isColorDraggingRef.current) {
        const dampingPerSecond = 4; // точно такое же затухание как у марок
        av = av * Math.max(0, 1 - dampingPerSecond * dt);
        if (Math.abs(av) < 0.001) av = 0;
      }

      // Интеграция
      residual += av * dt;

      // Щелчок по шагам и обновление индекса
      let index = carColorIndexRef.current;
      const half = STEP / 2;
      while (residual >= half) {
        residual -= STEP;
        index = (index + 1) % carColors.length;
      }
      while (residual <= -half) {
        residual += STEP;
        index = (index - 1 + carColors.length) % carColors.length;
      }

      if (index !== carColorIndexRef.current) {
        carColorIndexRef.current = index;
        setCarColorIndex(index);
        setSelectedCarColor(carColors[index]);
      }

      // Записываем обратно и рендерим
      colorAngularVelocityRef.current = av;
      colorRotationResidualRef.current = residual;
      setColorRotationOffset(residual);

      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [showCarColorForm, carColors]);

  // Цикл анимации с инерцией для барабана годов
  useEffect(() => {
    if (!showCarYearForm) return;
    const STEP = 0.4; // радиан на один элемент
    let rafId = 0;
    let last = performance.now();

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      // Текущее состояние
      let av = yearAngularVelocityRef.current; // рад/с
      let residual = yearRotationResidualRef.current; // рад

      // Трение (если не тащим мышью)
      if (!isYearDraggingRef.current) {
        const dampingPerSecond = 4; // точно такое же затухание как у марок
        av = av * Math.max(0, 1 - dampingPerSecond * dt);
        if (Math.abs(av) < 0.001) av = 0;
      }

      // Интеграция
      residual += av * dt;

      // Щелчок по шагам и обновление индекса
      let index = carYearIndexRef.current;
      const half = STEP / 2;
      while (residual >= half) {
        residual -= STEP;
        index = (index + 1) % carYears.length;
      }
      while (residual <= -half) {
        residual += STEP;
        index = (index - 1 + carYears.length) % carYears.length;
      }

      if (index !== carYearIndexRef.current) {
        carYearIndexRef.current = index;
        setCarYearIndex(index);
        setSelectedCarYear(carYears[index]);
      }

      // Записываем обратно и рендерим
      yearAngularVelocityRef.current = av;
      yearRotationResidualRef.current = residual;
      setYearRotationOffset(residual);

      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [showCarYearForm, carYears]);

  const handleDrumScroll = (e) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    const max = 3; // было 4 — мягче
    const impulse = 0.8; // было 1.2 — мягче
    const dir = e.deltaY > 0 ? 1 : -1;
    const next = angularVelocityRef.current + dir * impulse;
    angularVelocityRef.current = Math.max(-max, Math.min(max, next));
  };

  const handleDrumClick = () => {
    // Очень легкий импульс вниз при клике
    angularVelocityRef.current = Math.min(2, angularVelocityRef.current + 0.5);
  };

  // Обработчики для барабана моделей
  const handleModelDrumScroll = (e) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    const max = 3;
    const impulse = 0.8;
    const dir = e.deltaY > 0 ? 1 : -1;
    const next = modelAngularVelocityRef.current + dir * impulse;
    modelAngularVelocityRef.current = Math.max(-max, Math.min(max, next));
  };

  const handleModelDrumClick = () => {
    modelAngularVelocityRef.current = Math.min(2, modelAngularVelocityRef.current + 0.5);
  };

  // Обработчики для барабана цветов
  const handleColorDrumScroll = (e) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    const max = 3;
    const impulse = 0.8;
    const dir = e.deltaY > 0 ? 1 : -1;
    const next = colorAngularVelocityRef.current + dir * impulse;
    colorAngularVelocityRef.current = Math.max(-max, Math.min(max, next));
  };

  const handleColorDrumClick = () => {
    colorAngularVelocityRef.current = Math.min(2, colorAngularVelocityRef.current + 0.5);
  };

  // Обработчики для барабана годов
  const handleYearDrumScroll = (e) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    const max = 3;
    const impulse = 0.8;
    const dir = e.deltaY > 0 ? 1 : -1;
    const next = yearAngularVelocityRef.current + dir * impulse;
    yearAngularVelocityRef.current = Math.max(-max, Math.min(max, next));
  };

  const handleYearDrumClick = () => {
    yearAngularVelocityRef.current = Math.min(2, yearAngularVelocityRef.current + 0.5);
  };

  const getClientY = (event) => {
    if (event?.touches?.length) return event.touches[0].clientY;
    if (event?.changedTouches?.length) return event.changedTouches[0].clientY;
    return event?.clientY ?? 0;
  };

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    isDraggingRef.current = true;
    setDragStartY(getClientY(e));
    if (e.cancelable) {
      e.preventDefault();
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    const currentY = getClientY(e);
    const deltaY = currentY - dragStartY;
    // Чувствительность: радиан на пиксель (мягче)
    const sensitivity = 0.006; // было 0.01
    // Немедленно вращаем остаток, чтобы следовать за мышью
    rotationResidualRef.current += (-deltaY) * sensitivity;
    // Задаем скорость пропорционально движению
    angularVelocityRef.current = (-deltaY) * sensitivity * 18; // было 25
    setDragStartY(currentY);
  }, [isDragging, dragStartY]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
    setDragStartY(0);
  }, []);

  // Глобальные обработчики для drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp, { passive: false });
      document.addEventListener('touchmove', handleMouseMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp, { passive: false });
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Обработчики drag для барабана моделей
  const [isModelDragging, setIsModelDragging] = useState(false);
  const [modelDragStartY, setModelDragStartY] = useState(0);

  const handleModelMouseDown = useCallback((e) => {
    setIsModelDragging(true);
    isModelDraggingRef.current = true;
    setModelDragStartY(getClientY(e));
    if (e.cancelable) {
      e.preventDefault();
    }
  }, []);

  const handleModelMouseMove = useCallback((e) => {
    if (!isModelDragging) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    const currentY = getClientY(e);
    const deltaY = currentY - modelDragStartY;
    const sensitivity = 0.006;
    modelRotationResidualRef.current += (-deltaY) * sensitivity;
    modelAngularVelocityRef.current = (-deltaY) * sensitivity * 25;
    setModelDragStartY(currentY);
  }, [isModelDragging, modelDragStartY]);

  const handleModelMouseUp = useCallback(() => {
    setIsModelDragging(false);
    isModelDraggingRef.current = false;
    setModelDragStartY(0);
  }, []);

  // Глобальные обработчики для drag моделей
  useEffect(() => {
    if (isModelDragging) {
      document.addEventListener('mousemove', handleModelMouseMove, { passive: false });
      document.addEventListener('mouseup', handleModelMouseUp, { passive: false });
      document.addEventListener('touchmove', handleModelMouseMove, { passive: false });
      document.addEventListener('touchend', handleModelMouseUp, { passive: false });
      
      return () => {
        document.removeEventListener('mousemove', handleModelMouseMove);
        document.removeEventListener('mouseup', handleModelMouseUp);
        document.removeEventListener('touchmove', handleModelMouseMove);
        document.removeEventListener('touchend', handleModelMouseUp);
      };
    }
  }, [isModelDragging, handleModelMouseMove, handleModelMouseUp]);

  // Обработчики drag для барабана цветов
  const [isColorDragging, setIsColorDragging] = useState(false);
  const [colorDragStartY, setColorDragStartY] = useState(0);

  const handleColorMouseDown = useCallback((e) => {
    setIsColorDragging(true);
    isColorDraggingRef.current = true;
    setColorDragStartY(getClientY(e));
    if (e.cancelable) {
      e.preventDefault();
    }
  }, []);

  const handleColorMouseMove = useCallback((e) => {
    if (!isColorDragging) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    const currentY = getClientY(e);
    const deltaY = currentY - colorDragStartY;
    const sensitivity = 0.006;
    colorRotationResidualRef.current += (-deltaY) * sensitivity;
    colorAngularVelocityRef.current = (-deltaY) * sensitivity * 25;
    setColorDragStartY(currentY);
  }, [isColorDragging, colorDragStartY]);

  const handleColorMouseUp = useCallback(() => {
    setIsColorDragging(false);
    isColorDraggingRef.current = false;
    setColorDragStartY(0);
  }, []);

  // Глобальные обработчики для drag цветов
  useEffect(() => {
    if (isColorDragging) {
      document.addEventListener('mousemove', handleColorMouseMove, { passive: false });
      document.addEventListener('mouseup', handleColorMouseUp, { passive: false });
      document.addEventListener('touchmove', handleColorMouseMove, { passive: false });
      document.addEventListener('touchend', handleColorMouseUp, { passive: false });
      
      return () => {
        document.removeEventListener('mousemove', handleColorMouseMove);
        document.removeEventListener('mouseup', handleColorMouseUp);
        document.removeEventListener('touchmove', handleColorMouseMove);
        document.removeEventListener('touchend', handleColorMouseUp);
      };
    }
  }, [isColorDragging, handleColorMouseMove, handleColorMouseUp]);

  // Обработчики drag для барабана годов
  const [isYearDragging, setIsYearDragging] = useState(false);
  const [yearDragStartY, setYearDragStartY] = useState(0);

  const handleYearMouseDown = useCallback((e) => {
    setIsYearDragging(true);
    isYearDraggingRef.current = true;
    setYearDragStartY(getClientY(e));
    if (e.cancelable) {
      e.preventDefault();
    }
  }, []);

  const handleYearMouseMove = useCallback((e) => {
    if (!isYearDragging) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    const currentY = getClientY(e);
    const deltaY = currentY - yearDragStartY;
    const sensitivity = 0.006;
    yearRotationResidualRef.current += (-deltaY) * sensitivity;
    yearAngularVelocityRef.current = (-deltaY) * sensitivity * 25;
    setYearDragStartY(currentY);
  }, [isYearDragging, yearDragStartY]);

  const handleYearMouseUp = useCallback(() => {
    setIsYearDragging(false);
    isYearDraggingRef.current = false;
    setYearDragStartY(0);
  }, []);

  // Глобальные обработчики для drag годов
  useEffect(() => {
    if (isYearDragging) {
      document.addEventListener('mousemove', handleYearMouseMove, { passive: false });
      document.addEventListener('mouseup', handleYearMouseUp, { passive: false });
      document.addEventListener('touchmove', handleYearMouseMove, { passive: false });
      document.addEventListener('touchend', handleYearMouseUp, { passive: false });
      
      return () => {
        document.removeEventListener('mousemove', handleYearMouseMove);
        document.removeEventListener('mouseup', handleYearMouseUp);
        document.removeEventListener('touchmove', handleYearMouseMove);
        document.removeEventListener('touchend', handleYearMouseUp);
      };
    }
  }, [isYearDragging, handleYearMouseMove, handleYearMouseUp]);

  const handleBackClick = () => {
    if (showCarTechPassportForm) {
      setShowCarTechPassportForm(false);
      setShowCarLicensePlateForm(true);
    } else if (showCarLicensePlateForm) {
      setShowCarLicensePlateForm(false);
      setShowCarYearForm(true);
    } else if (showCarYearForm) {
      setShowCarYearForm(false);
      setShowCarColorForm(true);
    } else if (showCarColorForm) {
      setShowCarColorForm(false);
      setShowCarModelForm(true);
    } else if (showCarModelForm) {
      setShowCarModelForm(false);
      setShowCarBrandForm(true);
    } else if (showCarBrandForm) {
      setShowCarBrandForm(false);
      setShowSelfieForm(true);
    } else if (showRDAZone) {
      setShowRDAZone(false);
      setShowSelfieForm(true);
    } else if (showSelfieForm) {
      setShowSelfieForm(false);
      setShowDriverLicenseBackForm(true);
    } else if (showDriverLicenseBackForm) {
      setShowDriverLicenseBackForm(false);
      setShowDriverLicenseForm(true);
    } else if (showDriverLicenseForm) {
      setShowDriverLicenseForm(false);
      setShowPassportBackForm(true);
    } else if (showPassportBackForm) {
      setShowPassportBackForm(false);
      setShowPassportForm(true);
    } else if (showPassportForm) {
      setShowPassportForm(false);
      setShowRDZone(true);
    } else if (showRDZone) {
      setShowRDZone(false);
      setShowEmailVerificationForm(true);
    } else if (showEmailVerificationForm) {
      setShowEmailVerificationForm(false);
      setShowEmailForm(true);
      // НЕ очищаем email-код
    } else if (showEmailForm) {
      setShowEmailForm(false);
      setShowPhoneVerificationForm(true);
      // НЕ очищаем email - setUserEmail('');
    } else if (showPhoneVerificationForm) {
      setShowPhoneVerificationForm(false);
      setShowPhoneForm(true);
      // НЕ очищаем телефон и код
    } else if (showPhoneForm) {
      setShowPhoneForm(false);
      setShowSurnameForm(true);
      // НЕ очищаем телефон - setUserPhone('');
    } else if (showSurnameForm) {
      setShowSurnameForm(false);
      setShowInputForm(true);
      // НЕ очищаем фамилию - setUserSurname('');
    }
  };

  return (
    <div className="Scene">
      {/* Боковое меню (домик) — заглушка */}
      {isMenuDrawerOpen && (
        <>
          <div className="drawer-backdrop" onClick={() => setIsMenuDrawerOpen(false)} aria-hidden="true" />
          <div className="drawer-panel drawer-panel-left">
            <div className="drawer-header-block">
              <span className="drawer-title-text">DRIVERS WORLD</span>
              <button type="button" className="drawer-close" onClick={() => setIsMenuDrawerOpen(false)} aria-label="close">×</button>
            </div>
            <div className="drawer-menu-items">
              <div className="drawer-menu-item drawer-menu-item-placeholder">{t.menuComingSoon}</div>
            </div>
            <div className="drawer-footer-block">
              <span className="drawer-footer-text">{t.rights}</span>
              <span className="drawer-footer-sub">{t.allRightsReserved}</span>
            </div>
          </div>
        </>
      )}

      {/* Боковая панель выбора языка (глобус) */}
      {isLangDrawerOpen && (
        <>
          <div className="drawer-backdrop" onClick={() => setIsLangDrawerOpen(false)} aria-hidden="true" />
          <div className="drawer-panel drawer-panel-right">
            <div className="drawer-header-block">
              <span className="drawer-title-text">{t.drawerTitle}</span>
              <button type="button" className="drawer-close" onClick={() => setIsLangDrawerOpen(false)} aria-label="close">×</button>
            </div>
            <div className="drawer-menu-items">
              {LANGUAGES.map(({ code, nameNative }) => (
                <button
                  key={code}
                  type="button"
                  className={`drawer-menu-item drawer-menu-item-btn ${language === code ? 'drawer-menu-item-active' : ''}`}
                  onClick={() => {
                    setLanguage(code);
                    setIsLangDrawerOpen(false);
                  }}
                >
                  {nameNative}
                </button>
              ))}
            </div>
            <div className="drawer-footer-block">
              <span className="drawer-footer-text">{t.rights}</span>
              <span className="drawer-footer-sub">{t.allRightsReserved}</span>
            </div>
          </div>
        </>
      )}

      {!started && <img className="bg" src={`${assets.img}/stars_background.png`} alt="" />}

      {(!showCybermine) && (
        <>
          <button type="button" className="corner left-top" onClick={() => setIsMenuDrawerOpen(true)} aria-label="menu">
            <img src={`${assets.img}/menu.png`} alt="menu" />
          </button>
          <button type="button" className="corner right-top" onClick={() => setIsLangDrawerOpen(true)} aria-label="language">
            <img src={`${assets.img}/globe.png`} alt="globe" />
          </button>
        </>
      )}

      {/* Заголовок фиксированно сверху по центру на стартовом экране */}
      {!started && (
        <img className="title-top-initial" src={`${assets.img}/driver_world.png`} alt="Drivers World" />
      )}

      {!started && (
      <div className="center-stack">
        <button className="start-button" onClick={handleStart} aria-label="start">
          <img className="one-blue" src={`${assets.img}/one_blue_icon.png`} alt="one" />
          <img className="dw" src={`${assets.img}/dw_icon.png`} alt="dw" />
        </button>
      </div>
      )}

      {showPlanet && (
        <div className={`overlay ${planetFading ? 'fade-out' : ''}`}>
          <video
            ref={planetRef}
            src={`${assets.videos}/planet.mp4`}
            className="video full"
            muted={muted}
            onTimeUpdate={handlePlanetTime}
            onEnded={handlePlanetEnded}
            playsInline
          />
        </div>
      )}

      {showCybermine && (
        <div className="cybermine-layer">
          <video
            ref={cybermineRef}
            src={`${assets.videos}/cybermine.mp4`}
            className="video full"
            loop
            playsInline
            preload="auto"
          />
          
          {/* Скрытый audio элемент для музыки регистрации */}
          <audio
            ref={musicRef}
            src={`${assets.music}/Epic Heroic.mp3`}
            loop
            preload="auto"
            style={{ display: 'none' }}
          />

          <img
            className={`top-title from-center ${showTopTitle ? 'to-top-title' : ''}`}
            src={`${assets.img}/driver_world.png`}
            alt="Drivers World"
          />

          <img
            className={`pro-drivers from-center ${showProDrivers ? 'to-top-pro' : ''}`}
            src={`${assets.img}/pro_drivers.png`}
            alt="Pro Drivers"
          />

          {showCornerIcons && (
            <>
              <button type="button" className="corner left-top appear" onClick={() => setIsMenuDrawerOpen(true)} aria-label="menu">
                <img src={`${assets.img}/menu.png`} alt="menu" />
              </button>
              <button type="button" className="corner right-top appear" onClick={() => setIsLangDrawerOpen(true)} aria-label="language">
                <img src={`${assets.img}/globe.png`} alt="globe" />
              </button>
              <button className="mute left-bottom appear" onClick={toggleMute} aria-label="mute">
                <img src={`${assets.img}/${muted ? 'mute' : 'unmute'}.png`} alt="mute" />
              </button>
            </>
          )}

          {showWelcome && (
            <div className="welcome">WELCOME</div>
          )}

          {showSecretBlock && (
            <div className={`secret ${hideSecret ? 'hide' : ''}`}>
              <div className="secret-title">{t.secretTitle}</div>
              <div className="secret-card">
                <img src={`${assets.img}/splash_info.webp`} alt="info" />
                <div className="secret-overlay top">{t.zoneRL}</div>
                <div className="secret-overlay center">{t.zoneRegistration}</div>
                <div className="secret-overlay bottom">{t.zonePersonalData}</div>
                
              </div>
            </div>
          )}

          {showFormBlocks && (
            <div className="form-blocks show">
              <div className="form-block">
                <FormBorder fill="rgb(184, 110, 247)" mirror />
                <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                  <defs></defs>
                  <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                </svg>
                <div className="form-content">{t.welcomeForm}</div>
                <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
              </div>

              <div className="form-block clickable" onClick={handleAgreeClick}>
                <FormBorder fill="rgb(255, 255, 255)" />
                <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                  <defs></defs>
                  <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                </svg>
                <div className="form-content agree-text">{t.agree}</div>
                <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
              </div>
            </div>
          )}

          {showInputForm && (
            <div className="input-form-container show show">
              <div className="main-input-block">
                <div className="file-border-container">
                  <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                  <div className="main-input-content">
                    {showInputForm && (userName)}
                    {showSurnameForm && (
                      <span>{userName} {userSurname}</span>
                    )}
                    {showPhoneForm && (
                      <div className="name-phone-container">
                        <div className="full-name">{userName} {userSurname}</div>
                        <div className="phone-display">
                          {userPhone ? `+41 ${userPhone}` : ''}
                        </div>
                      </div>
                    )}
                    {(showEmailForm || showEmailVerificationForm) && (
                      <div className="name-phone-email-container">
                        <div className="full-name">{userName} {userSurname}</div>
                        <div className="phone-display">
                          {userPhone ? `+41 ${userPhone}` : ''}
                        </div>
                        <div className="email-display">
                          {userEmail}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterName}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <input 
                    type="text" 
                    className="name-input" 
                    placeholder=""
                    value={userName}
                    onChange={handleInputChange}
                    onKeyDown={(e) => e.key === 'Enter' && userName && handleArrowClick()}
                  />
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма ввода фамилии */}
          {showSurnameForm && (
            <div className="input-form-container show">
              <div className="main-input-block">
                <div className="file-border-container">
                  <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                  <div className="main-input-content">
                    <span>{userName} {userSurname}</span>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterSurname}</div>
                <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <input 
                    type="text" 
                    className="name-input" 
                    placeholder=""
                    value={userSurname}
                    onChange={handleSurnameChange}
                    onKeyDown={(e) => e.key === 'Enter' && userSurname && handleSurnameArrowClick()}
                  />
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Анимированная стрелка - отдельно от формы, в углу сайта */}
          {showInputForm && userName && (
            <div className="animated-arrow-site-corner" onClick={handleArrowClick} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Форма ввода телефона */}
          {showPhoneForm && (
            <div className="input-form-container show">
              <div className="main-input-block">
                <div className="file-border-container">
                  <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                  <div className="main-input-content">
                    <div className="name-phone-container">
                      <div className="full-name">{userName} {userSurname}</div>
                      <div className="phone-display">
                        {userPhone ? `+41 ${userPhone}` : ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterPhone}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="phone-input-container">
                    <img className="swiss-flag" src={`${assets.img}/swiss.png`} alt="Swiss flag" />
                    <span className="phone-prefix">+41</span>
                    <input 
                      type="text" 
                      className="phone-input" 
                      value={userPhone}
                      onChange={handlePhoneChange}
                      onKeyDown={(e) => e.key === 'Enter' && userPhone && handlePhoneArrowClick()}
                    />
                  </div>
                <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Анимированная стрелка для формы фамилии */}
          {showSurnameForm && userSurname && (
            <div className="animated-arrow-site-corner" onClick={handleSurnameArrowClick} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Форма ввода email */}
          {showEmailForm && (
            <div className="input-form-container show">
              <div className="main-input-block">
                <div className="file-border-container">
                  <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                  <div className="main-input-content">
                    <div className="name-phone-email-container">
                      <div className="full-name">{userName} {userSurname}</div>
                      <div className="phone-display">
                        {userPhone ? `+41 ${userPhone}` : ''}
                      </div>
                      <div className="email-display">
                        {userEmail}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterEmail}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <input 
                    type="email" 
                    className="name-input" 
                    value={userEmail}
                    onChange={handleEmailChange}
                    onKeyDown={(e) => e.key === 'Enter' && userEmail && isValidEmail(userEmail) && handleEmailArrowClick()}
                  />
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма подтверждения email по коду */}
          {showEmailVerificationForm && (
            <div className="input-form-container show">
              <div className="main-input-block">
                <div className="file-border-container">
                  <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                  <div className="main-input-content">
                    <div className="name-phone-email-container">
                      <div className="full-name">{userName} {userSurname}</div>
                      <div className="phone-display">
                        {userPhone ? `+41 ${userPhone}` : ''}
                      </div>
                      <div className="email-display">
                        {userEmail}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterEmailCode}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <input
                    type="text"
                    className="name-input"
                    value={emailVerificationCode}
                    onChange={handleEmailVerificationChange}
                    onKeyDown={(e) => e.key === 'Enter' && emailVerificationCode.length === 6 && handleEmailVerificationArrowClick()}
                  />
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма подтверждения телефона по SMS-коду */}
          {showPhoneVerificationForm && (
            <div className="input-form-container show">
              <div className="main-input-block">
                <div className="file-border-container">
                  <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                  <div className="main-input-content">
                    <div className="name-phone-container">
                      <div className="full-name">{userName} {userSurname}</div>
                      <div className="phone-display">
                        {userPhone ? `+41 ${userPhone}` : ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterSmsCode}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <input
                    type="text"
                    className="name-input"
                    value={phoneVerificationCode}
                    onChange={handlePhoneVerificationChange}
                    onKeyDown={(e) => e.key === 'Enter' && phoneVerificationCode.length === 6 && handlePhoneVerificationArrowClick()}
                  />
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Анимированная стрелка для формы телефона */}
          {showPhoneForm && userPhone && (
            <div className="animated-arrow-site-corner" onClick={handlePhoneArrowClick} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для формы кода подтверждения */}
          {((showPhoneVerificationForm && phoneVerificationCode.length === 6) || (showEmailVerificationForm && emailVerificationCode.length === 6)) && (
            <div
              className="animated-arrow-site-corner"
              onClick={showEmailVerificationForm ? handleEmailVerificationArrowClick : handlePhoneVerificationArrowClick}
              style={{cursor: 'pointer'}}
            >
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Зона RD - РЕГИСТРАЦИИ ДОКУМЕНТОВ */}
          {showRDZone && (
            <div className={`secret ${hideRDZone ? 'hide' : ''}`} onClick={handleRDZoneClick}>
              <div className="secret-card">
                <img src={`${assets.img}/splash_info.webp`} alt="info" />
                <div className="secret-overlay top">{t.zoneRD}</div>
                <div className="secret-overlay center">{t.zoneRegistration}</div>
                <div className="secret-overlay bottom">{t.zoneDocuments}</div>
              </div>
            </div>
          )}

          {/* Форма загрузки документов */}
          {showPassportForm && (
            <div className="input-form-container show passport-form">
              <div className="passport-two-blocks">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <img className="passport-example-image" src={`${assets.img}/form_select_passport_front.jpg`} alt="Пример паспорта" />
                    </div>
                  </div>
                </div>

                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      {selectedPassportImage ? (
                        <img className="uploaded-passport-display" src={selectedPassportImage} alt="Загруженный паспорт" />
                      ) : (null)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.passportFront}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="upload-container">
                    <label htmlFor="passport-upload" className="upload-label-styled">
                      {t.selectImages}
                    </label>
                    <input 
                      id="passport-upload"
                      type="file" 
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{display: 'none'}}
                    />
                  </div>
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма загрузки обратной стороны паспорта */}
          {showPassportBackForm && (
            <div className="input-form-container show passport-form">
              <div className="passport-two-blocks">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <img className="passport-example-image" src={`${assets.img}/form_select_passport_back.jpg`} alt="Пример обратной стороны паспорта" />
                    </div>
                  </div>
                </div>

                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      {selectedPassportBackImage ? (
                        <img className="uploaded-passport-display" src={selectedPassportBackImage} alt="Загруженная обратная сторона паспорта" />
                      ) : (null)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.passportBack}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="upload-container">
                    <label htmlFor="passport-back-upload" className="upload-label-styled">
                      {t.selectImages}
                    </label>
                    <input 
                      id="passport-back-upload"
                      type="file" 
                      accept="image/*"
                      onChange={handlePassportBackUpload}
                      style={{display: 'none'}}
                    />
                  </div>
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма загрузки водительского удостоверения */}
          {showDriverLicenseForm && (
            <div className="input-form-container show passport-form">
              <div className="passport-two-blocks">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <img className="passport-example-image" src={`${assets.img}/driver_licence_front.png`} alt="Пример водительского удостоверения" />
                    </div>
                  </div>
                </div>

                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      {selectedDriverLicenseImage ? (
                        <img className="uploaded-passport-display" src={selectedDriverLicenseImage} alt="Загруженное водительское удостоверение" />
                      ) : (null)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.driverLicense}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="upload-container">
                    <label htmlFor="driver-license-upload" className="upload-label-styled">
                      {t.selectImages}
                    </label>
                    <input 
                      id="driver-license-upload"
                      type="file" 
                      accept="image/*"
                      onChange={handleDriverLicenseUpload}
                      style={{display: 'none'}}
                    />
                  </div>
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма загрузки обратной стороны водительского удостоверения */}
          {showDriverLicenseBackForm && (
            <div className="input-form-container show passport-form">
              <div className="passport-two-blocks">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <img className="passport-example-image" src={`${assets.img}/driver_licence_back.png`} alt="Пример обратной стороны водительского удостоверения" />
                    </div>
                  </div>
                </div>

                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      {selectedDriverLicenseBackImage ? (
                        <img className="uploaded-passport-display" src={selectedDriverLicenseBackImage} alt="Загруженная обратная сторона водительского удостоверения" />
                      ) : (null)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.driverLicenseBack}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="upload-container">
                    <label htmlFor="driver-license-back-upload" className="upload-label-styled">
                      {t.selectImages}
                    </label>
                    <input 
                      id="driver-license-back-upload"
                      type="file" 
                      accept="image/*"
                      onChange={handleDriverLicenseBackUpload}
                      style={{display: 'none'}}
                    />
                  </div>
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма загрузки селфи */}
          {showSelfieForm && (
            <div className="input-form-container show passport-form">
              <div className="passport-two-blocks">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <img className="passport-example-image" src={`${assets.img}/driver_selfie.png`} alt="Пример селфи" />
                    </div>
                  </div>
                </div>

                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      {selectedSelfieImage ? (
                        <img className="uploaded-passport-display" src={selectedSelfieImage} alt="Загруженное селфи" />
                      ) : (null)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.selfie}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="upload-container">
                    <label htmlFor="selfie-upload" className="upload-label-styled">
                      {t.selectImages}
                    </label>
                    <input 
                      id="selfie-upload"
                      type="file" 
                      accept="image/*"
                      onChange={handleSelfieUpload}
                      style={{display: 'none'}}
                    />
                  </div>
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Зона RDA - РЕГИСТРАЦИИ АВТОПАРКА */}
          {showRDAZone && (
            <div className={`secret ${hideRDAZone ? 'hide' : ''}`} onClick={handleRDAZoneClick}>
              <div className="secret-card">
                <img src={`${assets.img}/splash_info.webp`} alt="info" />
                <div className="secret-overlay top">{t.zoneRDA}</div>
                <div className="secret-overlay center">{t.zoneRegistration}</div>
                <div className="secret-overlay bottom">{t.zoneFleet}</div>
              </div>
            </div>
          )}

          {/* Форма выбора марки автомобиля */}
          {showCarBrandForm && (
            <div className="input-form-container show car-brand-form">
              <div className="car-brand-two-blocks">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <div className="selected-car-brand">{selectedCarBrand}</div>
                    </div>
                  </div>
                </div>

                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content car-drum-container">
                      <div className="car-brand-title">{t.carBrandTitle}</div>
                      <div className="car-drum" 
                           style={{'--rotation-offset': `${rotationOffset}rad`}}
                           onWheel={handleDrumScroll} 
                           onClick={handleDrumClick}
                           onTouchStart={handleMouseDown}
                           onMouseDown={handleMouseDown}>
                        <div className={`drum-item up2 rotation-up2`}>
                          {carBrands[(carBrandIndex - 2 + carBrands.length) % carBrands.length]}
                        </div>
                        <div className={`drum-item up1 rotation-up1`}>
                          {carBrands[(carBrandIndex - 1 + carBrands.length) % carBrands.length]}
                        </div>
                        <div className={`drum-item current rotation-current`}>{selectedCarBrand}</div>
                        <div className={`drum-item down1 rotation-down1`}>
                          {carBrands[(carBrandIndex + 1) % carBrands.length]}
                        </div>
                        <div className={`drum-item down2 rotation-down2`}>
                          {carBrands[(carBrandIndex + 2) % carBrands.length]}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterCarBrand}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="car-brand-display">
                    {selectedCarBrand}
                  </div>
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма выбора модели автомобиля */}
          {showCarModelForm && (
            <div className="input-form-container show car-brand-form">
              <div className="car-brand-two-blocks">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <div className="selected-car-brand">
                        <div>{selectedCarBrand}</div>
                        <div>{selectedCarModel}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content car-drum-container">
                      <div className="car-brand-title">{t.carModelTitle}</div>
                      <div className="car-drum" 
                           style={{'--rotation-offset': `${modelRotationOffset}rad`}}
                           onWheel={handleModelDrumScroll} 
                           onClick={handleModelDrumClick}
                           onTouchStart={handleModelMouseDown}
                           onMouseDown={handleModelMouseDown}>
                        {carModels[selectedCarBrand] && carModels[selectedCarBrand].length > 0 && (
                          <>
                            <div className={`drum-item up2 rotation-up2`}>
                              {carModels[selectedCarBrand][(carModelIndex - 2 + carModels[selectedCarBrand].length) % carModels[selectedCarBrand].length]}
                            </div>
                            <div className={`drum-item up1 rotation-up1`}>
                              {carModels[selectedCarBrand][(carModelIndex - 1 + carModels[selectedCarBrand].length) % carModels[selectedCarBrand].length]}
                            </div>
                            <div className={`drum-item current rotation-current`}>{selectedCarModel}</div>
                            <div className={`drum-item down1 rotation-down1`}>
                              {carModels[selectedCarBrand][(carModelIndex + 1) % carModels[selectedCarBrand].length]}
                            </div>
                            <div className={`drum-item down2 rotation-down2`}>
                              {carModels[selectedCarBrand][(carModelIndex + 2) % carModels[selectedCarBrand].length]}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterCarModel}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="car-brand-display">
                    {selectedCarModel}
                  </div>
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма выбора цвета автомобиля */}
          {showCarColorForm && (
            <div className="input-form-container show car-brand-form">
              <div className="car-brand-two-blocks">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <div className="selected-car-brand">
                        <div>{selectedCarBrand}</div>
                        <div>{selectedCarModel}</div>
                        <div>{selectedCarColor}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content car-drum-container">
                      <div className="car-brand-title">{t.carColorTitle}</div>
                      <div className="car-drum" 
                           style={{'--rotation-offset': `${colorRotationOffset}rad`}}
                           onWheel={handleColorDrumScroll} 
                           onClick={handleColorDrumClick}
                           onTouchStart={handleColorMouseDown}
                           onMouseDown={handleColorMouseDown}>
                        <div className="drum-center-border"></div>
                        <div className={`drum-item up2 rotation-up2`}>
                          {carColors[(carColorIndex - 2 + carColors.length) % carColors.length]}
                        </div>
                        <div className={`drum-item up1 rotation-up1`}>
                          {carColors[(carColorIndex - 1 + carColors.length) % carColors.length]}
                        </div>
                        <div className={`drum-item current rotation-current`}>{selectedCarColor}</div>
                        <div className={`drum-item down1 rotation-down1`}>
                          {carColors[(carColorIndex + 1) % carColors.length]}
                        </div>
                        <div className={`drum-item down2 rotation-down2`}>
                          {carColors[(carColorIndex + 2) % carColors.length]}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterCarColor}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="car-brand-display">
                    {selectedCarColor}
                  </div>
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма выбора года выпуска автомобиля */}
          {showCarYearForm && (
            <div className="input-form-container show car-brand-form">
              <div className="car-brand-two-blocks">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <div className="selected-car-brand">
                        <div>{selectedCarBrand}</div>
                        <div>{selectedCarModel}</div>
                        <div>{selectedCarColor}</div>
                        <div>{selectedCarYear}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content car-drum-container">
                      <div className="car-brand-title">{t.carYearTitle}</div>
                      <div className="car-drum" 
                           style={{'--rotation-offset': `${yearRotationOffset}rad`}}
                           onWheel={handleYearDrumScroll} 
                           onClick={handleYearDrumClick}
                           onTouchStart={handleYearMouseDown}
                           onMouseDown={handleYearMouseDown}>
                        <div className="drum-center-border"></div>
                        <div className={`drum-item up2 rotation-up2`}>
                          {carYears[(carYearIndex - 2 + carYears.length) % carYears.length]}
                        </div>
                        <div className={`drum-item up1 rotation-up1`}>
                          {carYears[(carYearIndex - 1 + carYears.length) % carYears.length]}
                        </div>
                        <div className={`drum-item current rotation-current`}>{selectedCarYear}</div>
                        <div className={`drum-item down1 rotation-down1`}>
                          {carYears[(carYearIndex + 1) % carYears.length]}
                        </div>
                        <div className={`drum-item down2 rotation-down2`}>
                          {carYears[(carYearIndex + 2) % carYears.length]}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterCarYear}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="car-brand-display">
                    {selectedCarYear}
                  </div>
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма ввода госномера автомобиля */}
          {showCarLicensePlateForm && (
            <div className="input-form-container show">
              {/* Один большой блок по центру с информацией об автомобиле */}
              <div className="car-info-block">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <div className="selected-car-brand">
                        <div>{selectedCarBrand}</div>
                        <div>{selectedCarModel}</div>
                        <div>{selectedCarColor}</div>
                        <div>{selectedCarYear}</div>
                        <div>{carLicensePlate}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Нижние блоки с вводом госномера */}
              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.enterLicensePlate}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <input 
                    type="text" 
                    className="name-input" 
                    value={carLicensePlate}
                    onChange={(e) => setCarLicensePlate(e.target.value)}
                    placeholder="ZH 001001"
                  />
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Форма фото техпаспорта автомобиля */}
          {showCarTechPassportForm && (
            <div className="input-form-container show passport-form">
              <div className="passport-two-blocks">
                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      <img className="passport-example-image" src={`${assets.img}/pts_scan_front.jpg`} alt="Пример техпаспорта" />
                    </div>
                  </div>
                </div>

                <div className="main-input-block">
                  <div className="file-border-container">
                    <img className="file-border-bg" src={`${assets.img}/file_border.webp`} alt="border" />
                    <div className="main-input-content">
                      {selectedCarTechPassportImage ? (
                        <img className="uploaded-passport-display" src={selectedCarTechPassportImage} alt="Загруженный техпаспорт" />
                      ) : (null)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="horizontal-inputs">
                <div className="form-block input-block">
                  <FormBorder fill="rgb(184, 110, 247)" mirror />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormTitleField_background___XCQO form-bg form-bg-title" stroke="rgb(193 157 209)" style={{ transform: 'scale(-1, 1)' }}>
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="form-content">{t.techPassport}</div>
                  <img className="probe left-bottom" src={`${assets.img}/red_probe.webp`} alt="red probe" />
                </div>

                <div className="form-block input-block">
                  <FormBorder fill="rgb(255, 255, 255)" />
                  <svg width="100%" height="100%" version="1.1" fill="hsla(0,0%,49%,.555)" viewBox="0 0 24803.7 6717.9" className="FormInputBase_background__iRS5T form-bg form-bg-input" stroke="#fff">
                    <defs></defs>
                    <path d="M 24688.233 1149.102 L 24688.41 1134.921 L 19650.102 1147.965 L 19122.241 607.858 L 3870.925 607.635 L 144.702 2436.839 L 144.702 5874.276 L 12702.489 5881.393 L 13331.406 6408.827 L 24108.847 6409.396 L 24684.242 5940.661 L 24688.233 1149.102 Z" transform="matrix(-1, 0, 0, -1, 24833.112976, 7017.030762)"></path>
                  </svg>
                  <div className="upload-container">
                    <label htmlFor="carTechPassportInput" className="upload-label-styled">
                      {t.selectImages}
                    </label>
                    <input 
                      id="carTechPassportInput"
                      type="file" 
                      accept="image/*"
                      onChange={handleCarTechPassportImageChange}
                      style={{display: 'none'}}
                    />
                  </div>
                  <img className="probe right-bottom" src={`${assets.img}/green_probe.webp`} alt="green probe" />
                </div>
              </div>
            </div>
          )}

          {/* Финальный экран завершения регистрации */}
          {showRegistrationComplete && (
            <div className="secret">
              <div className="secret-card">
                <img src={`${assets.img}/splash_info.webp`} alt="info" />
                <div className="secret-overlay top">{t.registrationComplete}</div>
                <div className="secret-overlay center">{t.waitEmail}</div>
                <div className="secret-overlay bottom">{t.thanks}</div>
              </div>
            </div>
          )}

          {/* Анимированная стрелка для формы водительского удостоверения */}
          {showDriverLicenseForm && selectedDriverLicenseImage && (
            <div className="animated-arrow-site-corner" onClick={handleDriverLicenseNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для обратной стороны водительского удостоверения */}
          {showDriverLicenseBackForm && selectedDriverLicenseBackImage && (
            <div className="animated-arrow-site-corner" onClick={handleDriverLicenseBackNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для формы обратной стороны паспорта */}
          {showPassportBackForm && selectedPassportBackImage && (
            <div className="animated-arrow-site-corner" onClick={handlePassportBackNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для формы паспорта */}
          {showPassportForm && selectedPassportImage && (
            <div className="animated-arrow-site-corner" onClick={handlePassportNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для формы email */}
          {showEmailForm && userEmail && isValidEmail(userEmail) && (
            <div className="animated-arrow-site-corner" onClick={handleEmailArrowClick} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для формы селфи */}
          {showSelfieForm && selectedSelfieImage && (
            <div className="animated-arrow-site-corner" onClick={handleSelfieNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для выбора марки автомобиля */}
          {showCarBrandForm && (
            <div className="animated-arrow-site-corner" onClick={handleCarBrandNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для выбора модели автомобиля */}
          {showCarModelForm && (
            <div className="animated-arrow-site-corner" onClick={handleCarModelNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для выбора цвета автомобиля */}
          {showCarColorForm && (
            <div className="animated-arrow-site-corner" onClick={handleCarColorNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для выбора года автомобиля */}
          {showCarYearForm && (
            <div className="animated-arrow-site-corner" onClick={handleCarYearNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для формы госномера */}
          {showCarLicensePlateForm && carLicensePlate.trim() && (
            <div className="animated-arrow-site-corner" onClick={handleCarLicensePlateNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Анимированная стрелка для техпаспорта */}
          {showCarTechPassportForm && selectedCarTechPassportImage && !apiLoading && (
            <div className="animated-arrow-site-corner" onClick={handleCarTechPassportNext} style={{cursor: 'pointer'}}>
              <svg fill="rgb(152 239 111)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}

          {/* Индикатор загрузки документов */}
          {showCarTechPassportForm && apiLoading && (
            <div className="loading-indicator" style={{
              position: 'absolute',
              right: '20px',
              bottom: '20px',
              background: 'rgba(0,0,0,0.9)',
              color: 'white',
              padding: '15px 25px',
              borderRadius: '8px',
              fontSize: '14px',
              zIndex: 1000,
              border: '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <span>Сжатие и загрузка документов...</span>
              </div>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}

          {/* Анимированная стрелка НАЗАД - в левом нижнем углу */}
          {(showSurnameForm || showPhoneForm || showEmailForm || showEmailVerificationForm || showRDZone || showPassportForm || showPassportBackForm || showDriverLicenseForm || showDriverLicenseBackForm || showSelfieForm || showRDAZone || showCarBrandForm || showCarModelForm || showCarColorForm || showCarYearForm || showCarLicensePlateForm || showCarTechPassportForm) && (
            <div className="animated-arrow-back-corner" onClick={handleBackClick}>
              <svg fill="rgb(239 111 144)" stroke="#000" strokeWidth="5" className="arrow-svg" viewBox="0 0 2248 600">
                <style>{`
                  #arrow {animation: arrow linear infinite normal forwards} 
                  @keyframes arrow { 
                    0% {opacity: 0;} 
                    16.666667% {opacity: 0;} 
                    33.333333% {opacity: 1;} 
                    50% {opacity: 0.75;} 
                    66.666667% {opacity: 0.5;} 
                    83.333333% {opacity: 0.25;} 
                    100% {opacity: 0}
                  }
                `}</style>
                <g id="arrow" transform="translate(0 ,0)" style={{animationDuration: '1500ms', animationDelay: '1750ms', opacity: 1}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(333 ,0)" style={{animationDuration: '1500ms', animationDelay: '1500ms', opacity: 0.75}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(666 ,0)" style={{animationDuration: '1500ms', animationDelay: '1250ms', opacity: 0.5}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(999 ,0)" style={{animationDuration: '1500ms', animationDelay: '1000ms', opacity: 0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1332 ,0)" style={{animationDuration: '1500ms', animationDelay: '750ms', opacity: 0}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
                <g id="arrow" transform="translate(1665 ,0)" style={{animationDuration: '1500ms', animationDelay: '500ms', opacity: -0.25}}>
                  <polygon points="245,-300 450,-300 200, 0 450, 300 245, 300 0, 0" transform="translate(0 300)"></polygon>
                </g>
              </svg>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
